import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { SiteHeader, SiteFooter } from "@/components/Layout";
import CategorySheet from "@/components/CategorySheet";
import PostLightbox from "@/components/PostLightbox";
import { useI18n } from "@/i18n";

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function applyHomeOg(s) {
  if (!s) return;
  if (s.home_og_title) {
    document.title = s.home_og_title;
    setMeta("property", "og:title", s.home_og_title);
    setMeta("name", "twitter:title", s.home_og_title);
  }
  if (s.home_og_description) {
    setMeta("name", "description", s.home_og_description);
    setMeta("property", "og:description", s.home_og_description);
    setMeta("name", "twitter:description", s.home_og_description);
  }
  if (s.home_og_image) {
    const url = s.home_og_image.startsWith("http")
      ? s.home_og_image
      : `${process.env.REACT_APP_BACKEND_URL}${s.home_og_image}`;
    setMeta("property", "og:image", url);
    setMeta("name", "twitter:image", url);
  }
}

export default function Home() {
  const { t, lang, tagLabel } = useI18n();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const lightboxSlug = slug || null;

  useEffect(() => {
    setLoading(true);
    api.get(`/posts?lang=${lang}`).then((res) => setPosts(res.data)).catch(() => setPosts([])).finally(() => setLoading(false));
    api.get("/settings").then((r) => applyHomeOg(r.data)).catch(() => {});
  }, [lang]);

  // Unique tags across all posts (with counts) for the category sheet.
  const tagCounts = posts.reduce((acc, p) => {
    (p.tags || []).forEach((t) => { acc[t] = (acc[t] || 0) + 1; });
    return acc;
  }, {});
  const allTags = Object.keys(tagCounts).sort((a, b) => a.localeCompare(b));
  const shownPosts = activeTag ? posts.filter((p) => (p.tags || []).includes(activeTag)) : posts;

  const pickTag = (tag) => {
    setActiveTag(tag);
    setSheetOpen(false);
  };

  return (
    <div className="dd-root" data-testid="home-page">
      <SiteHeader />
      <main className="dd-feed feed-top">
        <div className="dd-wrap">
          <div className="dd-home-col">
          <h1 className="dd-explore-title" data-testid="explore-title">{t("exploreTitle")}</h1>
          {!loading && posts.length > 0 ? (
            <div className="dd-filter-row">
              <button className="dd-cat-btn" onClick={() => setSheetOpen(true)} data-testid="category-filter-btn">
                {activeTag ? tagLabel(activeTag) : t("allCategories")} <ChevronDown size={16} strokeWidth={2.5} />
              </button>
              <span className="dd-listings-count" data-testid="listings-count">{shownPosts.length} {t("listings")}</span>
            </div>
          ) : null}

          {loading ? (
            <p style={{ padding: "40px 0", color: "var(--muted)" }}>{t("loading")}</p>
          ) : posts.length === 0 ? (
            <p style={{ padding: "40px 0", color: "var(--muted)" }}>{t("noPosts")}</p>
          ) : shownPosts.length === 0 ? (
            <p style={{ padding: "40px 0", color: "var(--muted)" }} data-testid="no-tag-results">
              {t("noTag", { tag: tagLabel(activeTag) })}
            </p>
          ) : (
            shownPosts.map((post) => (
              <article
                className="dd-feed-item is-clickable"
                key={post.slug}
                data-testid={`feed-item-${post.slug}`}
                onClick={() => navigate(`/${post.slug}`)}
              >
                {post.featured_image ? (
                  <div className="dd-card-img" data-testid={`feed-thumb-${post.slug}`}>
                    <img src={imgSrc(post.featured_image)} alt={post.title} />
                  </div>
                ) : (
                  <div className="dd-card-img is-empty" aria-hidden="true" />
                )}
                <div className="dd-card-body">
                  <h2 data-testid={`feed-title-${post.slug}`}>{post.title}</h2>
                  {(post.tags && post.tags.length) ? (
                    <div className="dd-feed-tags" data-testid={`feed-tags-${post.slug}`}>
                      {post.tags.map((tag) => (
                        <span className="dd-tag-chip" key={tag} data-testid={`feed-tag-${post.slug}-${tag}`}>{tagLabel(tag)}</span>
                      ))}
                    </div>
                  ) : null}
                  {post.excerpt ? <p className="dd-excerpt">{post.excerpt}</p> : null}
                </div>
                <ChevronRight className="dd-card-chev" size={20} strokeWidth={2} aria-hidden="true" />
              </article>
            ))
          )}
          {!loading && posts.length > 0 ? (
            <p className="dd-feed-note" data-testid="feed-note">
              {t("feedNote")}
              <a href="mailto:hola@rising.casa" data-testid="feed-note-email">hola@rising.casa</a>
            </p>
          ) : null}
          </div>
        </div>
      </main>
      <SiteFooter />

      <CategorySheet
        open={sheetOpen}
        tags={allTags.map((tag) => ({ tag, count: tagCounts[tag] }))}
        total={posts.length}
        activeTag={activeTag}
        onPick={pickTag}
        onClose={() => setSheetOpen(false)}
      />

      {lightboxSlug ? (
        <PostLightbox
          slug={lightboxSlug}
          posts={shownPosts}
          onClose={() => navigate("/")}
          onNavigate={(s) => navigate(`/${s}`)}
        />
      ) : null}
    </div>
  );
}
